import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import LocalStorePicker from "./LocalStorePicker";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { useSort, usePersistedState } from "../lib/useSort";
import { SortChips } from "./SortControls";
import { windowPhrase } from "../lib/compare";
import PeriodBadge from "./PeriodBadge";

function UnitDelta({ pct }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 font-mono-bm text-xs text-neutral-400" title="No prior-period baseline">
        <Minus size={12} strokeWidth={2} />
        new
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700" : "text-red-700";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-xs font-semibold ${cls}`}>
      <Icon size={12} strokeWidth={2.5} />
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

export default function TopProducts({ range, initialOutletIds }) {
  const [outletIds, setOutletIds] = useState(initialOutletIds || []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = usePersistedState("bm-mode:top-products", "units"); // units | pct (revenue as % of total)

  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end, limit: 12 };
    if (outletIds.length > 0) params.outlet_ids = outletIds.join(",");
    api.get("/products/top", { params })
      .then((r) => { if (!cancel) setItems(r.data || []); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, outletIds.join(",")]);

  const sort = useSort("revenue", "desc", {
    units: (r) => r.units,
    growth: (r) => r.unit_growth_pct,
    avg_price: (r) => r.avg_price,
    revenue: (r) => r.revenue,
  }, [], "top-products");
  const sortedItems = sort.apply(items);
  const max = Math.max(1, ...sortedItems.map((i) => i.revenue));
  const totalRevenue = items.reduce((s, i) => s + (i.revenue || 0), 0);
  const revLabel = (v) =>
    mode === "pct"
      ? (totalRevenue ? `${((v / totalRevenue) * 100).toFixed(1)}%` : "—")
      : `$${Math.round(v).toLocaleString()}`;

  const buildExport = () => {
    const columns = [
      { key: "n", label: "#", align: "right" },
      { key: "name", label: "Product" },
      { key: "category", label: "Category" },
      { key: "units", label: "Units", align: "right" },
      { key: "growth", label: "Δ vs prior", align: "right" },
      { key: "avg_price", label: "Avg $", align: "right" },
      { key: "revenue", label: "Revenue", align: "right" },
    ];
    const flat = sortedItems.map((p, i) => ({
      n: i + 1,
      name: p.name,
      category: p.category || "",
      units: fmt.int(p.units),
      growth: p.unit_growth_pct != null ? fmt.pct1(p.unit_growth_pct) : "new",
      avg_price: `$${Math.round(p.avg_price)}`,
      revenue: fmt.money0(p.revenue),
    }));
    return {
      filenameBase: `top-products-${range?.label || "range"}`,
      meta: { title: "Best sellers", subtitle: `${range?.label || ""} · > 20 units sold in window · sorted by ${sort.sortKey} · ${sort.sortDir}` },
      columns,
      rows: flat,
    };
  };

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-4 md:p-6 min-w-0 overflow-hidden" data-testid="top-products">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div className="bm-eyebrow">Table 1 &middot; Top movers</div>
        <LocalStorePicker value={outletIds} onChange={setOutletIds} testId="top-products-stores" />
      </div>
      <h3 className="bm-headline text-3xl mb-1">The best sellers.</h3>
      <PeriodBadge range={range} className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-3">Filter: &gt; 20 units sold in window &middot; Unit Δ vs prior {windowPhrase(range?.start, range?.end)} &middot; Index vs store</p>
      <p className="hidden md:block text-xs text-neutral-500 mb-4 max-w-[560px] leading-relaxed" data-testid="index-definition">
        <span className="font-mono-bm text-neutral-700">idx</span> reads unit sales against the store's overall trend: <strong className="text-neutral-700">100</strong> = moved in line with the store, <strong className="text-emerald-700">above 100</strong> = outperforming, <strong className="text-[#c72c2c]">below 100</strong> = underperforming.
      </p>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <SortChips
          state={sort}
          options={[
            { k: "units", label: "Units" },
            { k: "growth", label: "Δ" },
            { k: "avg_price", label: "Avg $" },
            { k: "revenue", label: "Revenue" },
          ]}
          testIdPrefix="top-products-sort"
        />
        <div className="flex items-center gap-2">
          <div className="flex border border-[#0a0a0a]" role="group" aria-label="Revenue value mode" data-testid="top-products-mode-toggle">
            {[{ k: "units", label: "$" }, { k: "pct", label: "% of total" }].map((m, i) => (
              <button
                key={m.k}
                type="button"
                onClick={() => setMode(m.k)}
                data-testid={`top-products-mode-${m.k}`}
                title={m.k === "pct" ? "Show revenue as a % of total revenue" : "Show revenue in dollars"}
                className={`px-2.5 py-1.5 bm-eyebrow ${i > 0 ? "border-l border-[#0a0a0a]" : ""} ${mode === m.k ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <ExportButtons
            disabled={!items.length}
            testIdPrefix="top-products-export"
            onCSV={() => { const e = buildExport(); downloadCSV(e.filenameBase, e.columns, e.rows); }}
            onPDF={() => { const e = buildExport(); downloadPDF(e.filenameBase, e.meta, e.columns, e.rows); }}
          />
        </div>
      </div>

      {loading && items.length === 0 && <div className="text-sm text-neutral-500">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-neutral-600">No products crossed the threshold for this selection.</div>
      )}

      <div className="space-y-4">
        {sortedItems.map((p, i) => (
          <div key={p.product_id || p.name || i} className="border-t border-[#0a0a0a] pt-3" data-testid={`top-product-row-${i}`}>
            <div className="flex items-baseline justify-between gap-4">
              <div className="flex items-baseline gap-3 flex-1 min-w-0">
                <span className="bm-eyebrow text-neutral-500 w-8">#{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {p.rollup
                      ? <Link to="/tees" className="text-[#c72c2c] hover:underline" data-testid={`top-product-tees-link-${i}`}>{p.name} <span className="bm-eyebrow">· view designs →</span></Link>
                      : p.name}
                  </div>
                  <div className="bm-eyebrow text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="font-mono-bm text-neutral-700" data-testid={`top-product-units-${i}`}>
                      {p.units.toLocaleString()} units
                    </span>
                    <UnitDelta pct={p.unit_growth_pct} />
                    <span className="hidden md:inline">&middot;</span>
                    {p.index != null && p.index >= 0 ? (
                      <span
                        title={`Index vs store — actual units ÷ what this item would have sold if it merely tracked the store's overall unit trend (${p.store_growth_pct >= 0 ? "+" : ""}${p.store_growth_pct}%). 100 = in line · above = outperforming · below = underperforming.`}
                        className={`font-mono-bm px-1.5 py-0.5 border ${p.index >= 100 ? "text-emerald-700 border-emerald-700/40 bg-emerald-50/60" : "text-[#c72c2c] border-[#c72c2c]/40 bg-[#c72c2c]/5"}`}
                        data-testid={`top-product-index-${i}`}
                      >
                        idx {p.index}
                      </span>
                    ) : (
                      <span
                        title="Prior-period baseline too small to index reliably (< 15 units)."
                        className="font-mono-bm px-1.5 py-0.5 border text-neutral-400 border-neutral-300/50 bg-neutral-100/40"
                        data-testid={`top-product-index-${i}`}
                      >
                        idx —
                      </span>
                    )}
                    <span className="hidden md:inline">&middot;</span>
                    <span className="hidden md:inline">avg ${Math.round(p.avg_price)}</span>
                  </div>
                </div>
              </div>
              <div className="font-mono-bm text-sm font-semibold whitespace-nowrap" data-testid={`top-product-revenue-${i}`}>{revLabel(p.revenue)}</div>
            </div>
            <div className="mt-2 h-[2px] bg-[#0a0a0a]/10">
              <div className="h-full bg-[#0a0a0a]" style={{ width: `${(p.revenue / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
